import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ChevronRight, Check, X, ArrowRight, GraduationCap, BedDouble,
  Dumbbell, Sparkles, ShieldCheck, IndianRupee, Info, Download,
} from "lucide-react";

const INR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

/**
 * Pull fees data live from:
 *   • GET /api/public/admission  → Academy + Hostel packages
 *   • GET /api/services          → Pay-per-use facility rentals
 * No new backend endpoint needed — admin edits everything from existing CMS.
 */
export default function FeesStructure() {
  const [admission, setAdmission] = useState(null);
  const [services, setServices] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get("/public/admission").then((r) => setAdmission(r.data || {})).catch(() => setAdmission({}));
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => setServices([]));
  }, []);

  const loading = !admission || !services;

  return (
    <div data-testid="fees-structure-page" className="fees-printable">
      <Hero />
      <IntroStrip />
      {loading ? (
        <div className="min-h-[40vh] flex items-center justify-center text-white/40 text-sm font-mono uppercase tracking-widest">
          Loading fees…
        </div>
      ) : (
        <>
          <AcademyBlock data={admission.academic || {}} />
          <HostelBlock data={admission.hostel || {}} />
          <FacilityRentals services={services} />
          <FootNotes />
        </>
      )}
      <FinalCTA />
    </div>
  );
}

/* ------------------------------ HERO ------------------------------ */
function Hero() {
  return (
    <section className="relative border-b border-white/8 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1518091043644-c1d4457512c6?w=2000"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#0B1120]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <div className="font-mono text-[11px] uppercase tracking-[0.4em] brand-orange mb-4" data-testid="fees-eyebrow">
          // Transparent · GST-Registered Company · No Hidden Fees
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none" data-testid="fees-hero-title">
          Fees Structure
        </h1>
        <div className="mt-4 flex items-center justify-center gap-2 text-sm md:text-base font-medium text-white/70">
          <Link to="/" className="hover:brand-orange">Home</Link>
          <ChevronRight size={14} className="brand-orange" />
          <Link to="/academy" className="hover:brand-orange">Academy</Link>
          <ChevronRight size={14} className="brand-orange" />
          <span className="brand-orange">Fees Structure</span>
        </div>
        <div className="mt-6 no-print">
          <button
            onClick={() => window.print()}
            data-testid="fees-download-pdf"
            className="inline-flex items-center gap-2 bg-brand-orange text-black px-5 py-3 font-bold text-xs uppercase tracking-widest hover:bg-white transition"
          >
            <Download size={14} /> Download / Print as PDF
          </button>
          <div className="mt-2 text-[11px] font-mono text-white/50">
            Opens print dialog → choose <em>"Save as PDF"</em> to share on WhatsApp
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- INTRO STRIP --------------------------- */
function IntroStrip() {
  const items = [
    { icon: ShieldCheck, label: "GST Registered",     sub: "Compliant company · itemised GST on every bill" },
    { icon: IndianRupee, label: "No Hidden Fees",     sub: "Every rupee is itemised" },
    { icon: Sparkles,    label: "Membership Savings", sub: "Buy hours & save on rack rate" },
    { icon: Info,        label: "Refund Policy",      sub: "Security deposits refundable" },
  ];
  return (
    <section className="border-b border-white/8 bg-[#0F172A]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="flex items-start gap-3" data-testid={`fees-badge-${i}`}>
              <div className="w-9 h-9 shrink-0 grid place-items-center bg-brand-blue/20 border border-brand-blue/40">
                <Icon size={16} className="brand-blue" />
              </div>
              <div>
                <div className="font-display text-sm font-black uppercase tracking-tight">{it.label}</div>
                <div className="text-[11px] text-white/50 mt-0.5">{it.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ----------------------- ACADEMY BLOCK ----------------------- */
function AcademyBlock({ data }) {
  const fees = data.fees || [];
  const total = fees.reduce((a, f) => a + (parseFloat(f.amount) || 0), 0);
  const refund = fees.filter((f) => f.refundable).reduce((a, f) => a + (parseFloat(f.amount) || 0), 0);

  const includes = [
    "Structured coaching by certified professional coaches",
    "Access to all indoor & outdoor training facilities during your batch",
    "Fitness / conditioning slot with the sports-science team",
    "Ward performance report + parent access to attendance & fees",
    "Access to intra-academy tournaments and selection trials",
  ];

  return (
    <section className="py-14 md:py-20" data-testid="fees-academy-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <SectionHead
          eyebrow="Package · 01"
          title="Academy Admission"
          subtitle={data.subtitle || "Structured fees for our professional coaching programmes."}
          icon={GraduationCap}
        />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Fee table */}
          <div className="lg:col-span-3 border border-white/12 bg-[#131A2B]">
            <div className="bg-brand-blue px-5 py-3 flex items-center justify-between">
              <div className="font-display text-base md:text-lg font-black uppercase tracking-tight text-white">Fee Breakdown</div>
              <div className="font-mono text-[11px] text-white/85">
                Total {INR(total)}{refund > 0 && <span className="opacity-70"> · Refundable {INR(refund)}</span>}
              </div>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {fees.map((f, i) => (
                  <tr key={i} className="border-b border-white/6 last:border-b-0 hover:bg-white/4" data-testid={`academy-fee-row-${i}`}>
                    <td className="px-4 py-3 font-medium w-2/5">{f.label}</td>
                    <td className="px-4 py-3 text-right font-mono w-28">{INR(f.amount)}</td>
                    <td className="px-4 py-3 text-center w-32">
                      {f.refundable
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest brand-blue"><Check size={12} /> Refundable</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40"><X size={12} /> Non-refund</span>}
                    </td>
                  </tr>
                ))}
                {fees.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-10 text-center text-white/40 text-sm">Fee details will be published soon.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* What's Included */}
          <div className="lg:col-span-2 border border-white/12 bg-black/20 p-5">
            <div className="font-mono text-[11px] uppercase tracking-widest brand-orange mb-3">// What you get</div>
            <div className="font-display text-xl font-black uppercase tracking-tight mb-4">Facilities included</div>
            <ul className="space-y-2.5">
              {includes.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-white/80" data-testid={`academy-include-${i}`}>
                  <Check size={16} className="brand-orange shrink-0 mt-0.5" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------- HOSTEL BLOCK ----------------------- */
function HostelBlock({ data }) {
  const fees = data.fees || [];

  // Split into packages: Common one-time + With Education + Without Education
  const grouped = useMemo(() => {
    const g = { common: [], with_ed: [], without_ed: [] };
    fees.forEach((f) => {
      const pkg = (f.package || "").toLowerCase();
      if (pkg.includes("common")) g.common.push(f);
      else if (pkg.includes("without")) g.without_ed.push(f);
      else if (pkg.includes("with")) g.with_ed.push(f);
      else g.common.push(f);
    });
    return g;
  }, [fees]);

  const withEdIncludes = [
    "Boarding + lodging (2/3 sharing)",
    "3 meals + snacks (dietician-planned)",
    "Coaching in chosen sport",
    "School fees + transport",
    "Tuition support & study hall",
    "Warden 24×7 + laundry",
  ];
  const withoutEdIncludes = [
    "Boarding + lodging (2/3 sharing)",
    "3 meals + snacks (dietician-planned)",
    "Coaching in chosen sport",
    "Study hall",
    "Warden 24×7 + laundry",
  ];

  return (
    <section className="py-14 md:py-20 border-t border-white/8 bg-black/20" data-testid="fees-hostel-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <SectionHead
          eyebrow="Package · 02"
          title="Hostel Admission"
          subtitle={data.subtitle || "Residential facility for out-station wards."}
          icon={BedDouble}
        />

        {/* Common one-time */}
        {grouped.common.length > 0 && (
          <div className="mt-8 border border-brand-orange/60 bg-brand-orange/8" data-testid="hostel-common-block">
            <div className="bg-brand-orange px-5 py-3 flex items-center justify-between">
              <div className="font-display text-base md:text-lg font-black uppercase tracking-tight text-black">Common · One-time</div>
              <div className="font-mono text-[11px] text-black/80">Paid once at enrolment</div>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {grouped.common.map((f, i) => (
                  <tr key={i} className="border-b border-white/6 last:border-b-0">
                    <td className="px-4 py-3 font-medium">{f.label}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{f.description || ""}</td>
                    <td className="px-4 py-3 text-right font-mono w-32">{INR(f.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* With / Without Education compare */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <HostelPackageCard
            title="With Education"
            tone="blue"
            rows={grouped.with_ed}
            includes={withEdIncludes}
            testid="hostel-with-ed"
          />
          <HostelPackageCard
            title="Without Education"
            tone="orange"
            rows={grouped.without_ed}
            includes={withoutEdIncludes}
            testid="hostel-without-ed"
          />
        </div>

        {/* Wallet note */}
        {data.wallet_note && (
          <div className="mt-8 border-l-4 border-brand-orange bg-brand-orange/10 p-5 md:p-6" data-testid="hostel-wallet-note">
            <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-2">Ward Wallet · Imprest System</div>
            <p className="text-sm md:text-base text-white/85 leading-relaxed">{data.wallet_note}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function HostelPackageCard({ title, tone, rows, includes, testid }) {
  const total = rows.reduce((a, r) => a + (parseFloat(r.amount) || 0), 0);
  const isBlue = tone === "blue";
  const headerCls = isBlue ? "bg-brand-blue text-white" : "bg-brand-orange text-black";
  const badgeCls = isBlue ? "brand-blue border-brand-blue/60" : "brand-orange border-brand-orange/60";

  return (
    <div className="border border-white/12 bg-[#131A2B]" data-testid={testid}>
      <div className={`${headerCls} px-5 py-3 flex items-center justify-between`}>
        <div className="font-display text-base md:text-lg font-black uppercase tracking-tight">{title}</div>
        <div className={`font-mono text-[11px] ${isBlue ? "text-white/85" : "text-black/80"}`}>
          {rows.length ? `Total ${INR(total)}` : "Configure fees in admin"}
        </div>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((f, i) => (
            <tr key={i} className="border-b border-white/6 last:border-b-0">
              <td className="px-4 py-3 font-medium w-1/2">
                {f.label}
                {f.refundable && (
                  <div className={`inline-block ml-2 text-[9px] font-mono uppercase tracking-widest ${badgeCls} border px-1.5 py-0.5`}>
                    Refundable
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-right font-mono w-32">{INR(f.amount)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td className="px-4 py-6 text-center text-white/40 text-xs">No fees configured yet.</td></tr>
          )}
        </tbody>
      </table>
      <div className="border-t border-white/10 p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2.5">// What's included</div>
        <ul className="space-y-1.5">
          {includes.map((s, i) => (
            <li key={i} className="flex gap-2 text-[13px] text-white/80">
              <Check size={14} className={isBlue ? "brand-blue shrink-0 mt-0.5" : "brand-orange shrink-0 mt-0.5"} />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ----------------------- FACILITY RENTALS ----------------------- */
function FacilityRentals({ services }) {
  const active = (services || []).filter((s) => s.active !== false);
  if (active.length === 0) return null;

  return (
    <section className="py-14 md:py-20 border-t border-white/8" data-testid="fees-facilities-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <SectionHead
          eyebrow="Package · 03"
          title="Pay-Per-Use Facility Rentals"
          subtitle="Book any facility on an hourly / per-over basis — no admission required."
          icon={Dumbbell}
        />

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {active.map((s, i) => (
            <div
              key={s.slug || i}
              className="border border-white/12 bg-[#131A2B] hover:border-brand-blue/60 transition-colors overflow-hidden"
              data-testid={`fees-facility-${s.slug}`}
            >
              {s.image_url && (
                <div className="h-32 w-full overflow-hidden bg-black/40">
                  <img src={s.image_url} alt={s.name} className="w-full h-full object-cover opacity-80" />
                </div>
              )}
              <div className="p-4">
                <div className="font-display text-base font-black uppercase tracking-tight leading-tight">{s.name}</div>
                {s.description && (
                  <div className="text-[12px] text-white/60 mt-1.5 line-clamp-2">{s.description}</div>
                )}
                <div className="mt-3 flex items-baseline gap-2">
                  {(() => {
                    // Honour admin's Billing Unit — matrix-based services
                    // should show "From ₹X" rather than the empty unit_price.
                    const bu = s.billing_unit;
                    let big = INR(s.unit_price);
                    let sub = s.unit_label || `per ${bu || "hour"}`;
                    if (bu === "ball" || (Array.isArray(s.ball_pricing) && s.ball_pricing.length && !(s.unit_price > 0))) {
                      const prices = (s.ball_pricing || []).flatMap((b) => (b.tiers || []).map((t) => parseFloat(t.price) || 0)).filter((p) => p > 0);
                      if (prices.length) { big = `From ${INR(Math.min(...prices))}`; sub = "per over slot"; }
                    } else if (bu === "fixed_slot" || (Array.isArray(s.fixed_slots) && s.fixed_slots.length && !(s.unit_price > 0))) {
                      const prices = (s.fixed_slots || []).map((f) => parseFloat(f.price) || 0).filter((p) => p > 0);
                      if (prices.length) { big = `From ${INR(Math.min(...prices))}`; sub = "per session"; }
                      else { big = "On request"; sub = ""; }
                    }
                    return (
                      <>
                        <div className="font-display text-2xl font-black brand-orange">{big}</div>
                        {sub && <div className="text-[11px] font-mono uppercase tracking-widest text-white/50">{sub}</div>}
                      </>
                    );
                  })()}
                </div>
                <Link
                  to="/booking"
                  className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest brand-blue hover:text-white"
                >
                  Book Now <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------- FOOTNOTES ----------------------- */
function FootNotes() {
  const notes = [
    "All fees listed are exclusive of GST — the applicable GST is added on every invoice per current government rates.",
    "Security deposits (marked ‘Refundable’) are returned within 30–45 days of exit, subject to clearance of dues.",
    "Monthly fees are auto-invoiced on the 1st of every month and payable by the 5th.",
    "Sibling & one-time upfront-year discounts available — reach out to the admin office.",
  ];
  return (
    <section className="pb-14 md:pb-20" data-testid="fees-footnotes">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="border border-white/10 bg-black/30 p-5 md:p-6">
          <div className="font-mono text-[11px] uppercase tracking-widest text-white/60 mb-3">// Fine print</div>
          <ul className="space-y-2">
            {notes.map((n, i) => (
              <li key={i} className="flex gap-2.5 text-[13px] text-white/70">
                <Info size={14} className="brand-blue shrink-0 mt-0.5" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ----------------------- FINAL CTA ----------------------- */
function FinalCTA() {
  return (
    <section className="border-t border-white/8 bg-brand-orange text-black">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2">
          <div className="font-mono text-xs uppercase tracking-[0.3em] mb-3">// Have questions?</div>
          <h2 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
            Let's find the right<br />programme for your ward.
          </h2>
        </div>
        <div className="md:justify-self-end flex flex-col sm:flex-row gap-3">
          <Link
            to="/admission"
            data-testid="fees-cta-admission"
            className="bg-white text-black px-6 py-3 font-bold text-xs uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-2 justify-center"
          >
            View Admission
          </Link>
          <Link
            to="/enrol"
            data-testid="fees-cta-enrol"
            className="bg-black text-white px-6 py-3 font-bold text-xs uppercase tracking-widest hover:bg-white/90 hover:text-black inline-flex items-center gap-2 justify-center"
          >
            Enrol Your Ward <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ----------------------- SHARED SectionHead ----------------------- */
function SectionHead({ eyebrow, title, subtitle, icon: Icon }) {
  return (
    <div className="flex items-start gap-4 md:gap-6">
      {Icon && (
        <div className="hidden md:grid w-14 h-14 shrink-0 place-items-center bg-brand-blue/20 border border-brand-blue/50">
          <Icon size={22} className="brand-blue" />
        </div>
      )}
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.4em] brand-orange mb-2">// {eyebrow}</div>
        <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tighter leading-none">{title}</h2>
        {subtitle && <p className="mt-2 text-white/60 text-sm md:text-base max-w-2xl">{subtitle}</p>}
      </div>
    </div>
  );
}
